import { currentRuntime } from './runtime';

export type AnalyticsAssetClass = 'ALL' | 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO';

export const ANALYTICS_ASSET_CLASS_OPTIONS: AnalyticsAssetClass[] = ['ALL', 'STOCKS', 'FUTURES', 'FOREX', 'CRYPTO'];
export const DEFAULT_ANALYTICS_ASSET_CLASS: AnalyticsAssetClass = 'ALL';

export type TradingSettingsFormState = {
  feeDefaults: {
    stocks: string;
    futures: string;
    forex: string;
    crypto: string;
  };
  riskRules: {
    defaultRiskPerTrade: string;
    dailyLossLimit: string;
  };
  analyticsDefaultAssetClass: AnalyticsAssetClass;
};

export type TradingSettings = {
  feeDefaults: {
    stocks: number;
    futures: number;
    forex: number;
    crypto: number;
  };
  riskRules: {
    defaultRiskPerTrade: number;
    dailyLossLimit: number;
  };
  analyticsDefaultAssetClass: AnalyticsAssetClass;
};

export const TRADING_SETTINGS_STORAGE_KEY = 'tradeproof:trading:settings:v1';

export const tradingSettingsDefaults: TradingSettings = {
  feeDefaults: {
    stocks: 0,
    futures: 0,
    forex: 0,
    crypto: 0,
  },
  riskRules: {
    defaultRiskPerTrade: 0,
    dailyLossLimit: 0,
  },
  analyticsDefaultAssetClass: DEFAULT_ANALYTICS_ASSET_CLASS,
};

export function getRuntimeTradingSettingsDefaults(): TradingSettings {
  if (currentRuntime.tenant.id !== 'warrior-trading') return tradingSettingsDefaults;

  return {
    ...tradingSettingsDefaults,
    analyticsDefaultAssetClass: 'STOCKS',
  };
}

function applyRuntimeAnalyticsDefault(settings: TradingSettings): TradingSettings {
  if (currentRuntime.tenant.id !== 'warrior-trading') return settings;
  if (settings.analyticsDefaultAssetClass === 'STOCKS') return settings;

  return {
    ...settings,
    analyticsDefaultAssetClass: 'STOCKS',
  };
}

export type TradingSettingsEnvelope = {
  version: number;
  updatedAt: number;
  settings: TradingSettings;
};

const normalizeNonNegativeNumber = (value: unknown) => {
  const candidate = typeof value === 'string' ? value.trim() : value;
  const numericValue = Number(candidate);
  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : 0;
};

export const normalizeAnalyticsAssetClass = (value: unknown): AnalyticsAssetClass => {
  const normalized = String(value || '').trim().toUpperCase();
  return ANALYTICS_ASSET_CLASS_OPTIONS.includes(normalized as AnalyticsAssetClass)
    ? (normalized as AnalyticsAssetClass)
    : DEFAULT_ANALYTICS_ASSET_CLASS;
};

const readObject = (value: unknown) => (value && typeof value === 'object' ? value as Record<string, unknown> : undefined);

export function validateAndNormalizeTradingSettings(raw: unknown): TradingSettingsEnvelope {
  const now = Date.now();
  if (!raw || typeof raw !== 'object') {
    return { version: 1, updatedAt: now, settings: tradingSettingsDefaults };
  }

  const maybeEnvelope = raw as Record<string, unknown>;
  const maybeSettings = readObject(maybeEnvelope.settings) ?? maybeEnvelope;
  const maybeFeeDefaults = readObject(maybeSettings.feeDefaults) ?? maybeSettings;
  const maybeRiskRules = readObject(maybeSettings.riskRules) ?? maybeSettings;

  return {
    version: 1,
    updatedAt: now,
    settings: {
      feeDefaults: {
        stocks: normalizeNonNegativeNumber(maybeFeeDefaults.stocks),
        futures: normalizeNonNegativeNumber(maybeFeeDefaults.futures),
        forex: normalizeNonNegativeNumber(maybeFeeDefaults.forex),
        crypto: normalizeNonNegativeNumber(maybeFeeDefaults.crypto),
      },
      riskRules: {
        defaultRiskPerTrade: normalizeNonNegativeNumber(maybeRiskRules.defaultRiskPerTrade),
        dailyLossLimit: normalizeNonNegativeNumber(maybeRiskRules.dailyLossLimit),
      },
      analyticsDefaultAssetClass: normalizeAnalyticsAssetClass(
        maybeSettings.analyticsDefaultAssetClass
        ?? maybeSettings.defaultAssetClassForAnalytics
        ?? maybeSettings.defaultAssetClass
      ),
    },
  };
}

export function tradingSettingsToFormState(settings: TradingSettings): TradingSettingsFormState {
  return {
    feeDefaults: {
      stocks: String(settings.feeDefaults.stocks),
      futures: String(settings.feeDefaults.futures),
      forex: String(settings.feeDefaults.forex),
      crypto: String(settings.feeDefaults.crypto),
    },
    riskRules: {
      defaultRiskPerTrade: String(settings.riskRules.defaultRiskPerTrade),
      dailyLossLimit: String(settings.riskRules.dailyLossLimit),
    },
    analyticsDefaultAssetClass: settings.analyticsDefaultAssetClass,
  };
}

export function tradingSettingsFromFormState(formState: TradingSettingsFormState): TradingSettings {
  return {
    feeDefaults: {
      stocks: normalizeNonNegativeNumber(formState.feeDefaults.stocks),
      futures: normalizeNonNegativeNumber(formState.feeDefaults.futures),
      forex: normalizeNonNegativeNumber(formState.feeDefaults.forex),
      crypto: normalizeNonNegativeNumber(formState.feeDefaults.crypto),
    },
    riskRules: {
      defaultRiskPerTrade: normalizeNonNegativeNumber(formState.riskRules.defaultRiskPerTrade),
      dailyLossLimit: normalizeNonNegativeNumber(formState.riskRules.dailyLossLimit),
    },
    analyticsDefaultAssetClass: normalizeAnalyticsAssetClass(formState.analyticsDefaultAssetClass),
  };
}

export function readDefaultAnalyticsAssetClass(): AnalyticsAssetClass {
  return readTradingSettings().analyticsDefaultAssetClass;
}

export function readTradingSettings(): TradingSettings {
  const runtimeDefaults = getRuntimeTradingSettingsDefaults();
  if (typeof window === 'undefined') return runtimeDefaults;
  try {
    const raw = localStorage.getItem(TRADING_SETTINGS_STORAGE_KEY);
    if (!raw) return runtimeDefaults;
    return applyRuntimeAnalyticsDefault(validateAndNormalizeTradingSettings(JSON.parse(raw)).settings);
  } catch {
    return runtimeDefaults;
  }
}

export function saveTradingSettings(settings: TradingSettings): TradingSettingsEnvelope {
  const envelope = validateAndNormalizeTradingSettings({ version: 1, updatedAt: Date.now(), settings });
  envelope.settings = applyRuntimeAnalyticsDefault(envelope.settings);
  try {
    localStorage.setItem(TRADING_SETTINGS_STORAGE_KEY, JSON.stringify(envelope));
  } catch {}
  return envelope;
}

export function resetTradingSettings(): TradingSettingsEnvelope {
  const envelope = { version: 1, updatedAt: Date.now(), settings: getRuntimeTradingSettingsDefaults() };
  try {
    localStorage.setItem(TRADING_SETTINGS_STORAGE_KEY, JSON.stringify(envelope));
  } catch {}
  return envelope;
}
